import React, { useEffect, useState } from 'react'
import {
  Typography,
  Box,
  Tabs,
  Tab,
  Paper
} from '@mui/material'
import PropTypes from 'prop-types'
import { useNotificationReducer } from '../../hooks/useNotificationReducer'
import { useNotificationAPI } from '../../hooks/useNotificationAPI'
import { useToast } from '../../hooks'
import { ConfirmDialog, TabPanel, ToastNotification } from '../../components/atoms'
import AutomationsTab from '../../components/notifications/AutomationsTab'
import TemplatesTab from '../../components/notifications/TemplatesTab'
import AutomationDialog from '../../components/notifications/AutomationDialog'
import TemplateDialog from '../../components/notifications/TemplateDialog'
import CampaignsDialog from '../../components/notifications/CampaignsDialog'
import TestResultsDialog from '../../components/notifications/TestResultsDialog'
import TestPromptDialog from '../../components/notifications/TestPromptDialog'
import TemplatePreviewDialog from '../../components/notifications/TemplatePreviewDialog'
import EmailSendingProgressDialog from '../../components/notifications/EmailSendingProgressDialog'
import useEmailSendingProgress from '../../hooks/useEmailSendingProgress'
import styles from './SettingsNotifications.module.scss'

const SettingsNotifications = ({ userID }) => {
  const [state, actions] = useNotificationReducer()
  const api = useNotificationAPI(userID, actions)
  const emailProgress = useEmailSendingProgress()
  const { toast, showToast, closeToast } = useToast()
  const [confirmDialog, setConfirmDialog] = useState({ open: false, title: '', message: '', onConfirm: null })

  useEffect(() => {
    api.fetchAutomations()
    api.fetchTemplates()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    if (state.error) {
      showToast(state.error, 'error')
      actions.setError(null)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.error])

  const handleCreateAutomation = () => {
    actions.resetNewAutomation()
    actions.toggleDialog('createDialogOpen', true)
  }

  const handleEditAutomation = (automation) => {
    actions.setSelectedAutomation(automation)
    actions.toggleDialog('editDialogOpen', true)
  }

  const handleSaveAutomation = async (automation) => {
    const success = state.selectedAutomation
      ? await api.updateAutomation(automation)
      : await api.createAutomation(automation, state.templates)

    if (success) {
      actions.toggleDialog('createDialogOpen', false)
      actions.toggleDialog('editDialogOpen', false)
      actions.setSelectedAutomation(null)
      actions.resetNewAutomation()
    }
  }

  const handleDeleteAutomation = (automationId) => {
    setConfirmDialog({
      open: true,
      title: 'Delete Automation',
      message: 'Are you sure you want to delete this automation? This action cannot be undone.',
      confirmColor: 'error',
      onConfirm: async () => {
        setConfirmDialog(prev => ({ ...prev, open: false }))
        await api.deleteAutomation(automationId)
      }
    })
  }

  const handleToggleAutomation = async (automationId, enabled) => {
    await api.toggleAutomation(automationId, enabled)
  }

  const handleTestAutomation = (automation) => {
    actions.setSelectedAutomation(automation)
    actions.toggleDialog('testPromptDialogOpen', true)
  }

  const handleRunTest = async (testEmail, actualSend) => {
    const automation = state.selectedAutomation
    if (!automation) return

    actions.toggleDialog('testPromptDialogOpen', false)

    if (actualSend) {
      await emailProgress.startEmailSending(automation.id, testEmail)
    } else {
      actions.setTestMode({ enabled: false, email: testEmail })
      const results = await api.testAutomation(automation.id, null, false, testEmail)
      actions.setTestResults(results, automation.name, automation.id)
      actions.toggleDialog('testResultsDialogOpen', true)
    }
  }

  const handleManageCampaigns = async (automation) => {
    actions.setSelectedAutomation(automation)
    await api.fetchCampaignsForAutomation(automation.id)
    actions.toggleDialog('campaignsDialogOpen', true)
  }

  const handleToggleCampaign = async (campaignName, enabled) => {
    if (state.selectedAutomation) {
      await api.toggleCampaign(state.selectedAutomation.id, campaignName, enabled)
    }
  }

  const handleToggleAllCampaigns = async (enabled) => {
    if (state.selectedAutomation) {
      await api.toggleAllCampaigns(state.selectedAutomation.id, enabled)
    }
  }

  const handleCreateTemplate = () => {
    actions.resetNewTemplate()
    actions.toggleDialog('templateDialogOpen', true)
  }

  const handleEditTemplate = (template) => {
    actions.setSelectedTemplate(template)
    actions.toggleDialog('templateDialogOpen', true)
  }

  const handleSaveTemplate = async (template) => {
    const success = state.selectedTemplate
      ? await api.updateTemplate(template)
      : await api.createTemplate(template)

    if (success) {
      actions.toggleDialog('templateDialogOpen', false)
      actions.setSelectedTemplate(null)
      actions.resetNewTemplate()
    }
  }

  const handleDeleteTemplate = (templateId) => {
    setConfirmDialog({
      open: true,
      title: 'Delete Template',
      message: 'Are you sure you want to delete this template? This action cannot be undone.',
      confirmColor: 'error',
      onConfirm: async () => {
        setConfirmDialog(prev => ({ ...prev, open: false }))
        await api.deleteTemplate(templateId)
      }
    })
  }

  const handlePreviewTemplate = (template) => {
    actions.setSelectedTemplate(template)
    actions.toggleDialog('templatePreviewDialogOpen', true)
  }

  const handleDuplicateTemplate = (template) => {
    const duplicatedTemplate = {
      ...template,
      name: `${template.name} (Copy)`,
      id: undefined
    }
    actions.setSelectedTemplate(duplicatedTemplate)
    actions.toggleDialog('templateDialogOpen', true)
  }

  const closeCreateDialog = () => {
    actions.toggleDialog('createDialogOpen', false)
    actions.resetNewAutomation()
  }

  const closeEditDialog = () => {
    actions.toggleDialog('editDialogOpen', false)
    actions.setSelectedAutomation(null)
  }

  const closeTemplateDialog = () => {
    actions.toggleDialog('templateDialogOpen', false)
    actions.setSelectedTemplate(null)
    actions.resetNewTemplate()
  }

  const closeCampaignsDialog = () => {
    actions.toggleDialog('campaignsDialogOpen', false)
    actions.setSelectedAutomation(null)
  }

  const closeTestResultsDialog = () => {
    actions.toggleDialog('testResultsDialogOpen', false)
  }

  const closeTestPromptDialog = () => {
    actions.toggleDialog('testPromptDialogOpen', false)
    actions.setSelectedAutomation(null)
  }

  const closeTemplatePreviewDialog = () => {
    actions.toggleDialog('templatePreviewDialogOpen', false)
    actions.setSelectedTemplate(null)
  }

  return (
    <div className={styles.root}>
      <Box className={styles.pageHeader}>
        <Typography variant='h4' component='h1' className={styles.pageTitle}>
          Notifications
        </Typography>
        <Typography className={styles.pageSubtitle}>
          Manage automated email notifications and templates
        </Typography>
      </Box>

      <Paper className={styles.tabsPaper}>
        <Tabs
          value={state.tabValue}
          onChange={(e, newValue) => actions.setTabValue(newValue)}
          indicatorColor="primary"
          textColor="primary"
        >
          <Tab label="Automations" id='notifications-tab-0' aria-controls='notifications-tabpanel-0' />
          <Tab label="Email Templates" id='notifications-tab-1' aria-controls='notifications-tabpanel-1' />
        </Tabs>

        <TabPanel value={state.tabValue} index={0} idPrefix='notifications'>
          <AutomationsTab
            automations={state.automations}
            loading={state.loading}
            onCreateNew={handleCreateAutomation}
            onEdit={handleEditAutomation}
            onDelete={handleDeleteAutomation}
            onToggle={handleToggleAutomation}
            onTest={handleTestAutomation}
            onManageCampaigns={handleManageCampaigns}
          />
        </TabPanel>

        <TabPanel value={state.tabValue} index={1} idPrefix='notifications'>
          <TemplatesTab
            templates={state.templates}
            loading={state.loading}
            onCreateNew={handleCreateTemplate}
            onEdit={handleEditTemplate}
            onDelete={handleDeleteTemplate}
            onPreview={handlePreviewTemplate}
            onDuplicate={handleDuplicateTemplate}
          />
        </TabPanel>
      </Paper>

      {/* Create Automation Dialog */}
      <AutomationDialog
        open={state.createDialogOpen}
        onClose={closeCreateDialog}
        automation={state.newAutomation}
        templates={state.templates}
        onSave={handleSaveAutomation}
        isEditing={false}
      />

      {/* Edit Automation Dialog */}
      <AutomationDialog
        open={state.editDialogOpen}
        onClose={closeEditDialog}
        automation={state.selectedAutomation}
        templates={state.templates}
        onSave={handleSaveAutomation}
        isEditing={true}
      />

      {/* Template Dialog */}
      <TemplateDialog
        open={state.templateDialogOpen}
        onClose={closeTemplateDialog}
        template={state.selectedTemplate}
        onSave={handleSaveTemplate}
        isEditing={!!state.selectedTemplate?.id}
      />

      {/* Campaigns Dialog */}
      <CampaignsDialog
        open={state.campaignsDialogOpen}
        onClose={closeCampaignsDialog}
        automation={state.selectedAutomation}
        campaigns={state.campaigns}
        searchTerm={state.campaignSearchTerm}
        onSearchChange={actions.setCampaignSearchTerm}
        onToggleCampaign={handleToggleCampaign}
        onToggleAllCampaigns={handleToggleAllCampaigns}
      />

      {/* Test Prompt Dialog */}
      <TestPromptDialog
        open={state.testPromptDialogOpen}
        onClose={closeTestPromptDialog}
        onTest={handleRunTest}
        automationName={state.selectedAutomation?.name}
      />

      {/* Test Results Dialog */}
      <TestResultsDialog
        open={state.testResultsDialogOpen}
        onClose={closeTestResultsDialog}
        testResults={state.testResults}
        automationName={state.selectedAutomation?.name}
        automationId={state.selectedAutomation?.id}
      />

      {/* Template Preview Dialog */}
      <TemplatePreviewDialog
        open={state.templatePreviewDialogOpen}
        onClose={closeTemplatePreviewDialog}
        template={state.selectedTemplate}
      />

      {/* Email Sending Progress Dialog */}
      <EmailSendingProgressDialog
        open={emailProgress.isConnected || emailProgress.isComplete}
        onClose={() => {
          emailProgress.disconnect()
          api.fetchAutomations()
        }}
        automationName={state.selectedAutomation?.name}
        emailProgress={emailProgress.emailStatuses}
        isComplete={emailProgress.isComplete}
        totalEmails={emailProgress.progress.totalEmails}
        progress={emailProgress.progress}
        error={emailProgress.error}
      />

      {/* Confirm Dialog */}
      <ConfirmDialog
        open={confirmDialog.open}
        onConfirm={confirmDialog.onConfirm}
        onCancel={() => setConfirmDialog(prev => ({ ...prev, open: false }))}
        title={confirmDialog.title}
        message={confirmDialog.message}
        confirmLabel='Delete'
        confirmColor={confirmDialog.confirmColor || 'error'}
      />

      {/* Error Toast */}
      <ToastNotification
        open={toast.open}
        message={toast.message}
        severity={toast.severity}
        onClose={closeToast}
      />
    </div>
  )
}

SettingsNotifications.propTypes = {
  userID: PropTypes.string.isRequired
}

export default SettingsNotifications
